import { formatarDecimal } from './formatos'
import type { PontoPreco, ResultadoHibrido, ZonaPreco } from './simulacao'

/**
 * A Resposta do Simples puro × híbrido em palavras (ADR 0023 e 0024): título, frase e os três números,
 * na língua do cliente — "Simples como está" no lugar de "puro", "imposto" no lugar de CBS/IBS no texto
 * corrido. Tela, relatório técnico e Resumo do cliente usam esta função para dizer a MESMA coisa.
 *
 * Só formata: o dinheiro vem pronto do motor (`zona`), e a única conta aqui é a regra de três de
 * `porMil` e a variação em ppm.
 */

export const POSICOES_PRECO = ['piso', 'sugerido', 'teto'] as const
export type PosicaoPreco = (typeof POSICOES_PRECO)[number]

/** valor anual → o equivalente numa venda de R$ 1.000,00 de hoje */
export function porMil(valor: number, receitaHoje: number): number {
  return receitaHoje ? Math.round((valor * 100_000) / receitaHoje) : 0
}
/** variação de `x` sobre `base`, em ppm */
export function variacaoPpm(x: number, base: number): number {
  return base ? Math.round(((x - base) * 1_000_000) / base) : 0
}
/** ppm → "2,68%" (sem sinal) */
export const pctTexto = (ppm: number) => `${formatarDecimal(Math.abs(ppm), 4)}%`
/** diferenças abaixo de 0,05% são ruído de arredondamento: a frase diz "o mesmo" */
export const semDiferenca = (ppm: number) => Math.abs(ppm) < 500
export const reaisTexto = (centavos: number) => `${centavos < 0 ? '-' : ''}R$ ${formatarDecimal(Math.abs(centavos), 2)}`
const reais = reaisTexto
const comSinal = (ppm: number) => `${ppm < 0 ? '−' : '+'}${pctTexto(ppm)}`

export interface NumeroResposta {
  rotulo: string
  /** já formatado: "−4,08%", "o mesmo", "+R$ 14.322,40" */
  valor: string
  nota: string
  /** para a tela escolher a cor: negativo é bom para o cliente, positivo é bom para a empresa */
  variacao?: number
}

export interface ParcelaResposta {
  rotulo: string
  valor: number
  nota: string
}

export interface RespostaMontada {
  decisao: ZonaPreco['decisao']
  titulo: string
  frase: string
  posicao: PosicaoPreco
  ponto: PontoPreco
  /** ganho da empresa no ano contra continuar como está, já com as vendas ao consumidor final */
  ganhoAnual: number
  /** efeito das vendas a consumidor final e empresas sem crédito (nota igual à de hoje) */
  efeitoSemCredito: number
  variacaoPreco: number
  variacaoCustoCliente: number
  variacaoCustoClienteLucroReal: number
  /** efeito das vendas ao exterior (imunes) no resultado do ano */
  efeitoExportacao: number
  /** a venda-exemplo de R$ 1.000,00 de hoje, no ponto escolhido */
  porMilPonto: PontoPreco
  numeros: [NumeroResposta, NumeroResposta, NumeroResposta]
  /** de onde vem o resultado; as parcelas somam `ganhoAnual` ao centavo */
  parcelas: ParcelaResposta[]
}

/** A mesma resposta em qualquer um dos três pontos da faixa. Sem `zona`, não há resposta a montar. */
export function montarResposta(h: ResultadoHibrido, posicao: PosicaoPreco, z: ZonaPreco = h.zona!): RespostaMontada {
  return montarRespostaDoPonto(h, z[posicao], z.ganhoAnual[posicao], posicao, z)
}

/**
 * A mesma resposta num preço qualquer da faixa — é o que a tela usa enquanto o controle se move,
 * com o ponto interpolado e o ganho que ele produz. Os três pontos fixos passam por `montarResposta`.
 */
export function montarRespostaDoPonto(h: ResultadoHibrido, ponto: PontoPreco, ganhoAnual: number, posicao: PosicaoPreco, z: ZonaPreco = h.zona!): RespostaMontada {
  const efeitoSemCredito = z.semCredito ? z.semCredito.sobraMesmaNota - z.semCredito.sobraPuro : 0
  const efeitoExportacao = z.exportacao ? z.exportacao.sobraHibrido - z.exportacao.sobraPuro : 0
  const daEmpresa = ganhoAnual - efeitoSemCredito - efeitoExportacao
  const hibrido = z.decisao === 'HIBRIDO'
  const variacaoPreco = variacaoPpm(ponto.preco, z.receitaHoje)
  const variacaoCustoCliente = variacaoPpm(ponto.custoCliente, z.custoClientePuro)
  const variacaoCustoClienteLucroReal = variacaoPpm(ponto.custoCliente, z.custoClienteHoje.lucroReal)
  const mil = (v: number) => porMil(v, z.receitaHoje)
  const porMilPonto: PontoPreco = {
    preco: mil(ponto.preco),
    tributo: mil(ponto.tributo),
    nota: mil(ponto.nota),
    custoCliente: mil(ponto.custoCliente),
    sobra: mil(ponto.sobra),
  }

  const clientePaga = semDiferenca(variacaoCustoCliente)
    ? 'paga o mesmo que pagaria se a empresa continuasse como está'
    : `paga ${pctTexto(variacaoCustoCliente)} ${variacaoCustoCliente < 0 ? 'menos' : 'mais'} do que pagaria se a empresa continuasse como está`
  const frase = hibrido
    ? `Seu cliente empresa ${clientePaga}, credita todo o imposto da nota, e a empresa fica com ${reais(Math.abs(ganhoAnual))} ${ganhoAnual >= 0 ? 'a mais' : 'a menos'} no ano.`
    : `Mesmo no melhor caso — o cliente empresa pagando o mesmo que pagaria hoje —, o ganho nas vendas a empresas (${reais(daEmpresa)}) não cobre o que a empresa deixa de ganhar nas vendas ao consumidor final (${reais(efeitoSemCredito)}): o resultado do ano fica ${reais(Math.abs(ganhoAnual))} ${ganhoAnual >= 0 ? 'maior' : 'menor'}.`

  const numeros: [NumeroResposta, NumeroResposta, NumeroResposta] = [
    {
      rotulo: 'Seu preço, sem o imposto',
      valor: semDiferenca(variacaoPreco) ? 'o de hoje' : comSinal(variacaoPreco),
      nota: `um produto de ${reais(100_000)} sai por ${reais(porMilPonto.preco)} + imposto = nota de ${reais(porMilPonto.nota)}`,
      variacao: variacaoPreco,
    },
    {
      rotulo: 'O que o cliente empresa paga de verdade',
      valor: semDiferenca(variacaoCustoCliente) ? 'o mesmo' : comSinal(variacaoCustoCliente),
      nota: 'contra o que pagaria se a empresa continuasse como está',
      variacao: variacaoCustoCliente,
    },
    {
      rotulo: `Fica ${ganhoAnual >= 0 ? 'a mais' : 'a menos'} para a empresa no ano`,
      valor: `${ganhoAnual >= 0 ? '+' : '−'}${reais(Math.abs(ganhoAnual))}`,
      nota: 'contra continuar no Simples como está, com as vendas de hoje',
      variacao: ganhoAnual,
    },
  ]

  const parcelas: ParcelaResposta[] = [
    { rotulo: 'Vendas a empresas que se creditam', valor: daEmpresa, nota: 'inclui o crédito das suas compras' },
    ...(z.semCredito ? [{ rotulo: 'Vendas a consumidor final e sem crédito', valor: efeitoSemCredito, nota: 'a nota continua a mesma de hoje' }] : []),
    ...(z.exportacao ? [{ rotulo: 'Vendas ao exterior', valor: efeitoExportacao, nota: 'imunes; o crédito fica' }] : []),
  ]

  return {
    decisao: z.decisao,
    titulo: hibrido ? 'Optar pelo Simples híbrido' : 'Continuar no Simples como está',
    frase,
    posicao,
    ponto,
    ganhoAnual,
    efeitoSemCredito,
    efeitoExportacao,
    variacaoPreco,
    variacaoCustoCliente,
    variacaoCustoClienteLucroReal,
    porMilPonto,
    numeros,
    parcelas,
  }
}
